import { useCallback, useEffect, useState } from 'react';
import {
  getResumeFile,
  saveResumeData,
  getResumeData as getResumeDataFromServer,
  getOptionsData,
} from '../../../../util/APIUtils';
import { convertBase64ToBlob } from '../../../services/utilty';
import store, { showToast } from '../../../core/store';
import { SUCCESS, TABS } from '../../../services/constants';

const hasValue = (value) => value != null && String(value).trim() !== '';

/**
 * Careers GET résumé returns a raw Response with HTTP 204 when there is no saved résumé
 * (see `request` handling of status 204 in APIUtils.js).
 */
export const isResumeHttp204NoContent = (fetchResult) =>
  fetchResult != null &&
  typeof fetchResult === 'object' &&
  typeof fetchResult.status === 'number' &&
  fetchResult.status === 204;

/**
 * True when the SUCCESS envelope `payload` has any non-empty personal-info field or list rows.
 * Used only to decide whether to hydrate the form from the body (204 has no payload to inspect).
 */
export const resumePayloadHasStoredData = (payload) => {
  if (payload == null || typeof payload !== 'object') {
    return false;
  }
  const { personalInfo, workExperiences, educations, achievements, skills } = payload;
  const personalInfoFields = [
    personalInfo?.firstName,
    personalInfo?.lastName,
    personalInfo?.city,
    personalInfo?.state,
    personalInfo?.email,
    personalInfo?.phoneNumber,
    personalInfo?.aboutMe,
  ];

  return (
    personalInfoFields.some(hasValue) ||
    [workExperiences, educations, achievements, skills].some(
      (list) => Array.isArray(list) && list.length > 0,
    )
  );
};

export const useResumeBuilderHook = (props) => {
  const { history } = props;
  const [learnerName, setLearnerName] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [isFetchingPDF, setFetchingPDF] = useState(false);
  const [data, setResumeData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      location: { city: '', state: '' },
      phoneNumber: '',
      email: '',
      aboutMeSummary: '',
    },
    workExperiences: [],
    achievements: [],
    educations: [],
    skills: [],
  });
  const [stateOptions, setStatesOptions] = useState([]);
  const [degreeOptions, setDegreeOptions] = useState([]);
  const [skillOptions, setSkillsOptions] = useState([]);
  const [shouldShowResumeEmptyState, setShouldShowResumeEmptyState] = useState(false);

  const isEmbeddedLearnerResume =
    props.resumePersonKey != null && String(props.resumePersonKey).trim() !== '';

  const getPersonId = useCallback(
    () => String(props.resumePersonKey ?? props.match?.params?.id ?? '').trim(),
    [props.resumePersonKey, props.match?.params?.id]
  );
  /**
   * @name transformDataAfterFetch
   * @description Transform the data after fetching it from the server
   * @param {Object} data - The data to be transformed
   * @returns {Object} - The transformed data
   */
  const transformDataAfterFetch = (data) => {
    const personalInfo = data?.personalInfo ?? {};
    return {
      personalInfo: {
        firstName: personalInfo.firstName ?? '',
        lastName: personalInfo.lastName ?? '',
        location: {
          city: personalInfo.city || '',
          state: personalInfo.state || '',
        },
        email: personalInfo.email || '',
        phoneNumber: personalInfo.phoneNumber || '',
        aboutMeSummary: personalInfo.aboutMe || '',
      },
      workExperiences: (data?.workExperiences ?? []).map((item) => ({
        jobTitle: item.title,
        company: item.company,
        location: { city: item.city, state: item.state },
        startDate: { month: item.startDateMonth || '', year: item.startDateYear },
        endDate: { month: item.endDateMonth || '', year: item.endDateYear || '' },
        isCurrentlyWorking: item.stillWorks,
        jobDescription: item.jobDescription,
      })),
      educations: (data?.educations ?? []).map((item) => ({
        degree: item.degree,
        otherDegree: item.otherDegree || '',
        school: item.school,
        graduationYear: item.graduationYear,
        fieldOfStudy: item.fieldOfStudy || '',
      })),
      achievements: (data?.achievements ?? []).map((item) => ({
        name: item.title,
        organization: item.issuingOrganization,
        issueDate: { month: item.issuedDateMonth || '', year: item.issuedDateYear || '' },
        description: item.description,
      })),
      skills: [...(data?.skills ?? [])],
    };
  };

  /**
   * @name transformDataBeforeSave
   * @description Transform the data before saving it to the server
   * @param {Object} data - The data to be transformed
   * @returns {Object} - The transformed data
   */
  const transformDataBeforeSave = (data) => ({
    personalInfo: {
      firstName: data.personalInfo.firstName,
      lastName: data.personalInfo.lastName,
      city: data.personalInfo.location?.city,
      state: data.personalInfo.location?.state,
      email: data.personalInfo.email,
      phoneNumber: data.personalInfo.phoneNumber,
      aboutMe: data.personalInfo.aboutMeSummary,
    },
    workExperiences: data.workExperiences.map((item) => ({
      title: item.jobTitle,
      company: item.company,
      city: item.location?.city,
      state: item.location?.state,
      startDateMonth: item.startDate.month || null,
      startDateYear: item.startDate.year,
      endDateMonth: item.endDate?.month || null,
      endDateYear: item.endDate?.year || null,
      stillWorks: item.isCurrentlyWorking,
      jobDescription: item.jobDescription,
    })),
    educations: data.educations.map((item) => ({
      degree: item.degree,
      otherDegree: item.otherDegree,
      school: item.school,
      graduationYear: item.graduationYear,
      fieldOfStudy: item.fieldOfStudy,
    })),
    achievements: data.achievements.map((item) => ({
      title: item.name,
      issuingOrganization: item.organization,
      issuedDateMonth: item.issueDate?.month || null,
      issuedDateYear: item.issueDate?.year || null,
      description: item.description,
    })),
    skills: [...data.skills],
  });

  /**
   * @name getLearnerName
   * @desc Gets learner name from query param
   * @return
   */
  const getLearnerName = useCallback(() => {
    if (props.resumeLearnerDisplayName) {
      setLearnerName(props.resumeLearnerDisplayName);
      return;
    }
    const urlParams = new URLSearchParams(props.location.search);
    setLearnerName(urlParams.get('learnerName'));
  }, [props.resumeLearnerDisplayName, props.location.search]);

  const getResumeData = useCallback(async () => {
    setLoading(true);
    setShouldShowResumeEmptyState(false);
    try {
      const resumeRaw = await getResumeDataFromServer(getPersonId());
      const { payload: optionsData } = await getOptionsData();

      const http204NoResume = isResumeHttp204NoContent(resumeRaw);
      const envelopeSuccess = resumeRaw?.status === SUCCESS;
      const resumePayload = http204NoResume ? undefined : resumeRaw?.payload;

      if (http204NoResume || envelopeSuccess) {
        if (isEmbeddedLearnerResume && http204NoResume) {
          setShouldShowResumeEmptyState(true);
        }
        if (resumePayloadHasStoredData(resumePayload)) {
          setResumeData(transformDataAfterFetch(resumePayload));
        }
      }
      setStatesOptions(optionsData.states ?? []);
      setDegreeOptions(optionsData.degrees ?? []);
      setSkillsOptions(optionsData.skills ?? []);
    } catch (e) {
      console.error('Failed to load resume data:', e);
      store.dispatch(showToast('Failed to load resume data. Please try again.'));
    }
    setLoading(false);
  }, [getPersonId, isEmbeddedLearnerResume]);

  useEffect(() => {
    getResumeData();
    getLearnerName();
  }, [getResumeData, getLearnerName]);

  const onViewPDFClick = async () => {
    setFetchingPDF(true);
    try {
      const { status, payload } = await getResumeFile(getPersonId());
      if (status === SUCCESS) {
        window.open(convertBase64ToBlob(payload, 'application/pdf'), 'target=_blank');
      } else {
        throw new Error('Failed to fetch PDF: Invalid status');
      }
    } catch (e) {
      console.error('Failed to fetch resume PDF:', e);
      store.dispatch(showToast(`Something went wrong while fetching certificate.`));
    }

    setFetchingPDF(false);
  };

  const onGoBackClick = () => {
    history.push(`/manage?tab=${TABS.RESUME}`);
  };
  const onSave = async (data, callback) => {
    try {
      const { status } = await saveResumeData(getPersonId(), transformDataBeforeSave(data));
      if (status === SUCCESS) {
        callback();
        setResumeData(data);
        setShouldShowResumeEmptyState(false);
      } else {
        throw new Error('Failed to save resume: Invalid status');
      }
    } catch (error) {
      console.error('Failed to save resume data:', error);
      callback('Something Went Wrong!');
    }
  };

  return {
    isLoading,
    shouldShowResumeEmptyState,
    isFetchingPDF,
    data,
    stateOptions,
    degreeOptions,
    skillOptions,
    learnerName,
    onSave,
    onViewPDFClick,
    onGoBackClick,
  };
};
